// utils/MapboxCasinoLayers.ts

import mapboxgl, { Expression } from 'mapbox-gl';
import { casinoIdListEnum } from 'common/commonEnum';
import {
  addMapImage,
  LOTCIconArr,
} from 'components/FloorMapPackage/data/geoJson/lotc/mapboxImageLoad';
import {
  addMapEurekaImage,
  EurekaIconArr,
} from 'components/FloorMapPackage/data/geoJson/eureka/mapboxImageLoad';
import {
  addMapRWCImage,
  RWCIconArr,
} from 'components/FloorMapPackage/data/geoJson/rwc/mapboxImageLoad';
import {
  addMapSSCImage,
  SSCIconArr,
} from 'components/FloorMapPackage/data/geoJson/ssc/mapboxImageLoad';
import {
  addMapACHImage,
  ACHIconArr,
} from 'components/FloorMapPackage/data/geoJson/ach/mapboxImageLoad';
import {
  addMapJJCImage,
  JJCIconArr,
} from 'components/FloorMapPackage/data/geoJson/jjc/mapboxImageLoad';

export const setupCasinoLayers = (map: mapboxgl.Map, casinoId: number) => {
  switch (casinoId) {
    case casinoIdListEnum.LOTC:
      addMapImage(map);
      map.addLayer({
        id: 'labelLayer',
        type: 'symbol',
        source: 'labelSource',
        layout: {
          'icon-image': [
            'case',
            ['in', ['get', 'icon'], ['literal', LOTCIconArr.map((item) => item.name)]],
            ['to-string', ['get', 'icon']],
            '',
          ],
          'icon-size': generateIconSize(),
          'text-variable-anchor': ['top'],
          'text-radial-offset': generateTextOffset(),
          'text-field': ['to-string', ['get', 'text']],
          'text-size': generateTextSizeLOTC(),
          'text-justify': 'auto',
          'text-anchor': 'left',
          'text-padding': 0,
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Regular'],
        },
        paint: { 'text-color': '#f7f5f7', 'text-opacity': 0.5 },
      });
      break;

    case casinoIdListEnum.EUREKA:
      addMapEurekaImage(map);
      map.addLayer({
        id: 'lineLayerEureka',
        type: 'line',
        source: 'labelSource',
        layout: {},
        paint: { 'line-color': '#fff', 'line-width': 0 },
      });
      map.addLayer({
        id: 'labelLayerEureka',
        type: 'symbol',
        source: 'labelSource',
        layout: {
          'icon-image': [
            'case',
            ['in', ['get', 'autocad_block_name'], ['literal', EurekaIconArr.map((i) => i.name)]],
            ['to-string', ['get', 'autocad_block_name']],
            '',
          ],
          'icon-size': generateIconSize(),
          'icon-anchor': 'center',
          'icon-offset': [0, 0],
          'icon-allow-overlap': false,
          'icon-rotation-alignment': 'map',
          'text-allow-overlap': true,
          'text-field': ['to-string', ['get', 'autocad_text_string']],
          'text-padding': 0,
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Regular'],
          'text-offset': [0, 0],
          'text-anchor': 'center',
          'text-justify': 'center',
          'text-size': generateTextSizeEureka(),
          'text-rotation-alignment': 'map',
          'text-rotate': ['get', 'autocad_rotation'],
        },
        paint: { 'text-color': '#f7f5f7', 'text-opacity': 0.5 },
      });
      break;

    case casinoIdListEnum.RWC:
      addMapRWCImage(map);
      map.addLayer({
        id: 'labelLayerLineRWC',
        type: 'line',
        source: 'labelSource',
        layout: {},
        paint: { 'line-color': '#fff', 'line-width': 0 },
      });
      map.addLayer({
        id: 'labelLayerRWC',
        type: 'symbol',
        source: 'labelSource',
        layout: {
          'icon-image': [
            'case',
            ['in', ['get', 'autocad_block_name'], ['literal', RWCIconArr.map((i) => i.name)]],
            ['to-string', ['get', 'autocad_block_name']],
            '',
          ],
          'icon-size': generateIconSize(),
          'icon-anchor': 'center',
          'icon-offset': [0, 0],
          'icon-allow-overlap': false,
          'icon-rotation-alignment': 'map',
          'text-allow-overlap': true,
          'text-field': ['to-string', ['get', 'autocad_text_string']],
          'text-padding': 0,
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Regular'],
          'text-offset': [0, 0],
          'text-anchor': 'center',
          'text-justify': 'center',
          'text-size': generateTextSizeRWC(),
          'text-rotation-alignment': 'map',
          'text-rotate': ['get', 'autocad_rotation'],
        },
        paint: { 'text-color': '#f7f5f7', 'text-opacity': 0.5 },
      });
      break;
    case casinoIdListEnum.SSC:
      addMapSSCImage(map);
      map.addLayer({
        id: 'labelLayerLineSSC',
        type: 'line',
        source: 'labelSource',
        layout: {},
        paint: { 'line-color': '#fff', 'line-width': 0 },
      });
      map.addLayer({
        id: 'labelLayerSSC',
        type: 'symbol',
        source: 'labelSource',
        layout: {
          'icon-image': [
            'case',
            ['in', ['get', 'autocad_block_name'], ['literal', SSCIconArr.map((i) => i.name)]],
            ['to-string', ['get', 'autocad_block_name']],
            '',
          ],
          'icon-size': generateIconSize(),
          'icon-anchor': 'center',
          'icon-offset': [0, 0],
          'icon-allow-overlap': false,
          'icon-rotation-alignment': 'map',
          'text-allow-overlap': true,
          'text-field': ['to-string', ['get', 'autocad_text_string']],
          'text-padding': 0,
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Regular'],
          'text-offset': [0, 0],
          'text-anchor': 'center',
          'text-justify': 'center',
          'text-size': generateTextSizeSSC(),
          'text-rotation-alignment': 'map',
          'text-rotate': ['get', 'autocad_rotation'],
        },
        paint: { 'text-color': '#f7f5f7', 'text-opacity': 0.5 },
      });
      break;

    case casinoIdListEnum.ACH:
      addMapACHImage(map);
      map.addLayer({
        id: 'labelLayerLineACH',
        type: 'line',
        source: 'labelSource',
        layout: {},
        paint: { 'line-color': '#fff', 'line-width': 0 },
      });
      map.addLayer({
        id: 'labelLayerACH',
        type: 'symbol',
        source: 'labelSource',
        layout: {
          'icon-image': [
            'case',
            ['in', ['get', 'autocad_block_name'], ['literal', ACHIconArr.map((i) => i.name)]],
            ['to-string', ['get', 'autocad_block_name']],
            '',
          ],
          'icon-size': generateIconSizeACH(),
          'icon-anchor': 'center',
          'icon-offset': [0, 0],
          'icon-allow-overlap': false,
          'icon-rotation-alignment': 'map',
          'icon-rotate': ['get', 'autocad_rotation'],
          'text-allow-overlap': true,
          'text-field': ['to-string', ['get', 'autocad_text_string']],
          'text-padding': 0,
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Regular'],
          'text-offset': [
            'case',
            ['==', ['get', 'autocad_block_name'], '2nd_Floor'],
            ['literal', [-1, 0]],
            ['literal', [0, 0]],
          ],
          'text-anchor': [
            'case',
            ['==', ['get', 'autocad_block_name'], '2nd_Floor'],
            'left',
            'center',
          ],
          'text-justify': 'center',
          'text-size': generateTextSizeACH(),
          'text-rotation-alignment': 'map',
          'text-rotate': ['get', 'autocad_rotation'],
        },
        paint: { 'text-color': '#f7f5f7', 'text-opacity': 0.5 },
      });
      break;

    case casinoIdListEnum.JJC:
      addMapJJCImage(map);
      map.addLayer({
        id: 'labelLayerLineJJC',
        type: 'line',
        source: 'labelSource',
        layout: {},
        paint: { 'line-color': '#fff', 'line-width': 0 },
      });
      map.addLayer({
        id: 'labelLayerJJC',
        type: 'symbol',
        source: 'labelSource',
        layout: {
          'icon-image': [
            'case',
            ['in', ['get', 'autocad_block_name'], ['literal', JJCIconArr.map((i) => i.name)]],
            ['to-string', ['get', 'autocad_block_name']],
            '',
          ],
          'icon-size': generateIconSizeACH(),
          'icon-anchor': 'center',
          'icon-offset': [0, 0],
          'icon-allow-overlap': false,
          'icon-rotation-alignment': 'map',
          'icon-rotate': ['get', 'autocad_rotation'],
          'text-allow-overlap': true,
          'text-field': ['to-string', ['get', 'autocad_text_string']],
          'text-padding': 0,
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Regular'],
          'text-offset': ['literal', [0, 0]],
          'text-anchor': 'center',
          'text-justify': 'center',
          'text-size': generateTextSizeACH(),
          'text-rotation-alignment': 'map',
          'text-rotate': ['get', 'autocad_rotation'],
        },
        paint: { 'text-color': '#f7f5f7', 'text-opacity': 0.5 },
      });
      break;
  }
};

// Shared helpers
const generateIconSize = (): Expression => [
  'interpolate',
  ['linear'],
  ['zoom'],
  13,
  0.1,
  14,
  0.11,
  15,
  0.22,
  16,
  0.33,
  17,
  0.6,
  18,
  1.2,
  19,
  1.6,
];

const generateTextSizeLOTC = (): Expression => [
  'interpolate',
  ['linear'],
  ['zoom'],
  13,
  10,
  14,
  10,
  15,
  10,
  16,
  20,
  17,
  20,
  18,
  30,
  19,
  45,
  20,
  60,
  24,
  75,
];

const generateTextSizeEureka = (): Expression => [
  'interpolate',
  ['linear'],
  ['zoom'],
  13,
  8,
  14,
  8,
  15,
  11,
  16,
  15,
  17,
  24,
];

const generateTextSizeRWC = (): Expression => [
  'interpolate',
  ['linear'],
  ['zoom'],
  12,
  ['case', ['==', ['get', 'autocad_text_string'], 'Cashier'], 6, 8],
  13,
  ['case', ['==', ['get', 'autocad_text_string'], 'Cashier'], 6, 8],
  14,
  ['case', ['==', ['get', 'autocad_text_string'], 'Cashier'], 13, 8],
  15,
  ['case', ['==', ['get', 'autocad_text_string'], 'Cashier'], 21, 11],
  16,
  ['case', ['==', ['get', 'autocad_text_string'], 'Cashier'], 40, 15],
  17,
  ['case', ['==', ['get', 'autocad_text_string'], 'Cashier'], 64, 24],
];

const generateTextSizeSSC = (): Expression => [
  'interpolate',
  ['linear'],
  ['zoom'],
  12, 4,
  13, 4,
  14, 6,
  15, 10,
  16, 18,
  17, 28,
  18, 38,
  19, 48,
  20, 58,
];

const generateIconSizeACH = (): Expression => [
  'interpolate',
  ['linear'],
  ['zoom'],
  13, 0.08,
  14, 0.09,
  15, 0.18,
  16, 0.26,
  17, 0.45,
  18, 0.9,
  19, 1.2,
];

const generateTextSizeACH = (): Expression => {
  const isFloorLabel: Expression = [
    'any',
    ['==', ['get', 'autocad_text_string'], 'FIRST FLOOR'],
    ['==', ['get', 'autocad_block_name'], '1st_Floot'],
    ['==', ['get', 'autocad_block_name'], '2nd_Floor'],
  ];
  return [
    'interpolate',
    ['linear'],
    ['zoom'],
    12, ['case', isFloorLabel,  12,  4],
    13, ['case', isFloorLabel,  12,  4],
    14, ['case', isFloorLabel,  18,  6],
    15, ['case', isFloorLabel,  30, 10],
    16, ['case', isFloorLabel,  54, 18],
    17, ['case', isFloorLabel,  84, 28],
    18, ['case', isFloorLabel, 114, 38],
    19, ['case', isFloorLabel, 144, 48],
    20, ['case', isFloorLabel, 174, 58],
  ];
};

const generateTextOffset = (): Expression => [
  'interpolate',
  ['linear'],
  ['zoom'],
  13,
  1.5,
  14,
  1.5,
  15,
  1.5,
  16,
  1.8,
  17,
  4,
  18,
  4,
  19,
  4,
];
